import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, Input } from '@/shared/ui';
import { useSettings } from '@/shared/hooks/useSettings';
import { ReplicateClient } from '@/shared/api/replicate';
import {
  IMAGE_MODELS,
  LLM_MODELS,
  DEFAULT_IMAGE_MODEL,
  DEFAULT_LLM_MODEL,
} from '@/shared/catalog/models';

export function SettingsPage() {
  const [settings, update] = useSettings();
  const [tokenDraft, setTokenDraft] = useState(settings.replicateToken);

  const test = useMutation({
    mutationFn: async (token: string) => {
      const client = new ReplicateClient({ token });
      return client.testToken();
    },
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Settings</h1>

      <Card>
        <h2 className="font-medium">Replicate API token</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your token is stored only in this browser&apos;s localStorage and sent only to
          api.replicate.com. Do not paste tokens on a shared computer.
        </p>
        <div className="mt-4 flex gap-2">
          <Input
            type="password"
            placeholder="r8_..."
            value={tokenDraft}
            onChange={(e) => setTokenDraft(e.target.value)}
            autoComplete="off"
          />
          <Button
            variant="secondary"
            onClick={() => test.mutate(tokenDraft)}
            disabled={!tokenDraft || test.isPending}
          >
            {test.isPending ? 'Testing…' : 'Test'}
          </Button>
          <Button
            onClick={() => update({ replicateToken: tokenDraft })}
            disabled={tokenDraft === settings.replicateToken}
          >
            Save
          </Button>
        </div>
        {test.isSuccess ? (
          <p className="mt-2 text-sm text-emerald-400">Token works.</p>
        ) : test.isError ? (
          <p className="mt-2 text-sm text-destructive">{test.error?.message}</p>
        ) : null}
        <details className="mt-4 text-sm text-muted-foreground">
          <summary className="cursor-pointer list-none select-none text-foreground underline underline-offset-2 hover:text-foreground/80">
            How do I get a Replicate API token?
          </summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>
              Sign up at{' '}
              <a
                href="https://replicate.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                replicate.com
              </a>
              . The fastest way is to log in with GitHub; if you don&apos;t have a GitHub
              account yet, create one at{' '}
              <a
                href="https://github.com/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                github.com/signup
              </a>{' '}
              (you can sign up with your Google email).
            </li>
            <li>
              Open your{' '}
              <a
                href="https://replicate.com/account/api-tokens"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                API tokens page
              </a>
              .
            </li>
            <li>Copy the token (starts with <code>r8_</code>) and paste it above.</li>
          </ol>
        </details>
      </Card>

      <Card>
        <h2 className="font-medium">Defaults</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Default LLM model">
            <select
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              value={settings.defaultLlmModel}
              onChange={(e) =>
                update({ defaultLlmModel: e.target.value || DEFAULT_LLM_MODEL })
              }
            >
              {LLM_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Default image model">
            <select
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              value={settings.defaultImageModel}
              onChange={(e) =>
                update({ defaultImageModel: e.target.value || DEFAULT_IMAGE_MODEL })
              }
            >
              {IMAGE_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Default resolution (px, longest side)">
            <select
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              value={settings.defaultImageResolutionPx}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v === 1024 || v === 2048 || v === 4096) {
                  update({ defaultImageResolutionPx: v });
                }
              }}
            >
              <option value={1024}>1K (1024)</option>
              <option value={2048}>2K (2048)</option>
              <option value={4096}>4K (4096)</option>
            </select>
          </Field>
          <Field label="Images per template">
            <Input
              type="number"
              min={1}
              max={10}
              value={settings.defaultNumImagesPerTemplate}
              onChange={(e) =>
                update({
                  defaultNumImagesPerTemplate: clamp(Number(e.target.value), 1, 10),
                })
              }
            />
          </Field>
          <Field label="Concurrency cap">
            <Input
              type="number"
              min={1}
              max={16}
              value={settings.concurrencyCap}
              onChange={(e) => update({ concurrencyCap: clamp(Number(e.target.value), 1, 16) })}
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Number.isFinite(n) ? n : min));
}
